import withLifecycle from '@hocs/with-lifecycle';
import { bi } from '@wix/media-manager-sdk';
import {
  anyPass,
  contains,
  equals,
  filter,
  head,
  isNil,
  join,
  map,
  not,
  once,
  pathOr,
  pick,
  pipe,
  prop,
  reject,
  uniq,
  without,
} from 'ramda';
import { contained } from 'ramda-adjunct';
import React, { createContext } from 'react';
import { connect } from 'react-refetch';
import { withRouter } from 'react-router';
import {
  branch,
  compose,
  renderNothing,
  setDisplayName,
  withContext,
  withHandlers,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { MediaType } from '../../../constants/MediaType';
import {
  NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS,
  NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS_OLD,
  NAME_SPACE_SITE_FILES_TRANSLATIONS,
  NameSpace,
} from '../../../constants/NameSpace';
import { PerfMark } from '../../../constants/PerfMark';
import { ExternalSource, MainSource, SupportedExternalSourcesList } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { parentConnection, ParentConnectionEvent } from '../../../services/parentConnection';
import { getCiVersion } from '../../env/getCiVersion';
import { mark } from '../../env/performance';
import { isStandalone } from '../../env/utils';
import { getAdapter } from '../../media-manager-sdk/getAdapter';
import { isVisitorsUploads } from '../../media-manager-sdk/sources';
import { memoize } from '../../memoize';
import { parsePath } from '../../mmgrSettings/path';
import { removeUndefinedValues } from '../../object';
import { RouteBuilder, RouteMatcher } from '../../RouteHelpers';
import { parseSiteMediaToken, removeTemplatePart } from '../../siteMediaToken';
import { deserializeStringToArray } from '../../string';
import {
  createTrackMediaManagerLoadedSubscription,
  withTrackMediaManagerLoadedObservers,
} from '../bi/withAppLoadedAnalytics/withTrackMediaManagerLoadedObservers';
import { memoizeHandlers } from '../memoizeHandlers';
import { isValueChanged, onPropChange } from '../onPropChange';
import { withFirst, withoutFirst } from '../withFirst';
import { withLoadingErrorCatcher } from '../withLoadingErrorCatcher';
import { withoutRouter } from '../withoutRouter';
import { defaultMMGRSettings } from './defaultMMGRSettings';
import {
  getLocalConfig,
  isSecureContext,
  parseMaybeObject,
  parseMediaType,
  parseSettingsPath,
  parseTabsList,
  propsAreValid,
  selectTabsListForNamespace,
  trackMediaManagerOpened,
  validateIncomingSettings,
} from './utils';
import { withExperimentsSettings, withoutExperimentsSettings } from './withExperimentsSettings';
import {
  ConsentPolicyChangedMessage,
  contextTypes,
  IIncomingMMGRSettings,
  IMMGRSettingsProps,
  TIncomingTranslationsObject,
  TUserSettings,
} from './withMMGRSettings.types';
import { getInstance } from '../../getInstance';
import { EVENT_INSTANCE_EXPIRED, withAppEvents } from '../../../services/appEvents';

const emitAppIsReady = once(() => parentConnection.emit(ParentConnectionEvent.Ready));

const fallbackUserSettings = {} as TUserSettings;

const createMediaManagerSDKBILoggingInitializer = memoize((siteMediaToken) =>
  once(() =>
    bi(true, {
      siteId: pipe(parseSiteMediaToken, prop('metaSiteId'), removeTemplatePart)(siteMediaToken),
      version: `MMGR6-${getCiVersion()}`,
    }),
  ),
);

export const MMGRContext = createContext<IMMGRSettingsProps>({
  mmgrSettings: null,
  latestIncomingMMGRSettings: null,
});

export const enhanceMMGRSettingsProvider = compose<IMMGRSettingsProps, {}>(
  setDisplayName('enhanceMMGRSettingsProvider'),
  withLoadingErrorCatcher,
  withExperimentsSettings,
  withProps(({ _experimentsSettings }) => ({
    extendedDefaultMMGRSettings: {
      ...defaultMMGRSettings,
      ..._experimentsSettings,
    },
  })),
  withoutExperimentsSettings,
  withFirst('appOpenedAnalytics'),
  withAppEvents,
  withTrackMediaManagerLoadedObservers,
  withStateHandlers<IIncomingMMGRSettings, {}, Partial<IIncomingMMGRSettings> & { extendedDefaultMMGRSettings: any }>(
    ({ extendedDefaultMMGRSettings }) => ({
      latestIncomingMMGRSettings: getLocalConfig(),
      mmgrSettings: {
        ...extendedDefaultMMGRSettings,
        ...getLocalConfig(),
      },
      isFirstLoadingDone: false,
    }),
    {
      setMMGRSettings:
        (_, { extendedDefaultMMGRSettings, isFirst, setFirst, resetLoadingError, updateSettings$ }) =>
        (newMMGRSettings) => {
          validateIncomingSettings(newMMGRSettings);

          const mmgrSettings = {
            ...extendedDefaultMMGRSettings,
            ...getLocalConfig(),
            ...reject(anyPass([equals(''), isNil]), newMMGRSettings),
          };
          resetLoadingError();
          trackMediaManagerOpened({ isFirst, setFirst, mmgrSettings });

          updateSettings$.next();

          parentConnection.emit(ParentConnectionEvent.SettingsReady);
          return {
            latestIncomingMMGRSettings: newMMGRSettings,
            mmgrSettings,
          };
        },
      goToEmpty:
        ({ mmgrSettings }) =>
        () => ({ mmgrSettings: { ...mmgrSettings, path: RouteBuilder.Empty() } }),
      setMetaSiteToken:
        ({ mmgrSettings }) =>
        (metaSiteToken) => ({ mmgrSettings: { ...mmgrSettings, metaSiteToken } }),
      setFirstLoadingDone: () => (isFirstLoadingDone) => ({ isFirstLoadingDone }),
      updateConsentPolicy: () => (message: ConsentPolicyChangedMessage) => {
        if (!window.consentPolicyManager) {
          return;
        }
        window.consentPolicyManager.init({
          consentPolicy: message.data,
        });
      },
    },
  ),
  withLifecycle(() => {
    const addMarks = () => {
      mark(PerfMark.LoadStart);
      mark(PerfMark.OpenStart);
    };

    return {
      onDidMount({ updateSettings$, trackLoaded$, mmgrSettings, setMMGRSettings, goToEmpty, updateConsentPolicy }) {
        createTrackMediaManagerLoadedSubscription(updateSettings$, trackLoaded$);

        emitAppIsReady();
        parentConnection.on('settings', addMarks);
        parentConnection.on('settings', setMMGRSettings);
        if (!mmgrSettings.isIgnoreEmptyRouteEnabled) {
          parentConnection.on('empty', goToEmpty);
        }
        parentConnection.on('consentPolicyChanged', updateConsentPolicy);
      },
      onWillUnmount({ setMMGRSettings, goToEmpty, updateConsentPolicy, mmgrSettings }) {
        parentConnection.off('settings', addMarks);
        parentConnection.off('settings', setMMGRSettings);
        if (!mmgrSettings.isIgnoreEmptyRouteEnabled) {
          parentConnection.off('empty', goToEmpty);
        }
        parentConnection.off('consentPolicyChanged', updateConsentPolicy);
      },
    };
  }),
  withoutFirst,
  branch(pipe(propsAreValid, not), renderNothing),
  onPropChange<IMMGRSettingsProps>(
    (prevProps, nextProps) => prevProps.mmgrSettings.siteMediaToken !== nextProps.mmgrSettings.siteMediaToken,
    ({ mmgrSettings }) => {
      createMediaManagerSDKBILoggingInitializer(mmgrSettings.siteMediaToken)();
    },
  ),
  connect<any>(({ mmgrSettings, setLoadingError }) => {
    const { siteMediaToken, metaSiteToken } = mmgrSettings;

    return {
      loadUserSettings: () => ({
        userSettings: {
          value: async () => {
            const getInitialMetaSiteToken = async (): Promise<string | null> => {
              if (metaSiteToken) {
                return metaSiteToken;
              }

              return window.__INSTANCE__;
            };

            const initialMetaSiteToken = await getInitialMetaSiteToken();

            const privateSiteAdapter = getAdapter(MainSource.SiteFiles, {
              ...mmgrSettings,
              metaSiteToken: initialMetaSiteToken,
            });
            privateSiteAdapter.correlationId.randomize();

            return privateSiteAdapter.user.getSitesInfo().then((response) => {
              const {
                sitesNumber,
                hasVisitorUploads,
                hasContributors,
                hasWixVideoUploads,
                team,
                country,
                searches,
                parentAccountId,
                storageQuotaGracePeriodEnd,
                enterpriseTabs,
              } = response.data;

              return {
                metaSiteToken: initialMetaSiteToken,
                parentAccountId,
                hasVisitorUploads,
                hasContributors,
                sitesNumber,
                hasWixVideoUploads,
                team,
                hasMultipleSites: sitesNumber > 1,
                country,
                storageQuotaGracePeriodEnd: storageQuotaGracePeriodEnd || {},
                searches,
                enterpriseTabs,
              };
            });
          },
          comparison: siteMediaToken,
          andCatch: (error) => {
            console.error(error);
            setLoadingError(error);

            return {};
          },
          force: true,
        },
      }),
    };
  }),
  withHandlers({
    updateMetaSiteToken:
      ({ mmgrSettings, setMetaSiteToken }) =>
      async () => {
        const { siteMediaToken } = mmgrSettings;
        const metaSiteToken = await getInstance(parseSiteMediaToken(siteMediaToken).metaSiteId);

        setMetaSiteToken(metaSiteToken);
      },
  }),
  memoizeHandlers('loadUserSettings'),
  withLifecycle({
    onDidMount({ loadUserSettings, appEvents, updateMetaSiteToken }) {
      loadUserSettings();
      appEvents.on(EVENT_INSTANCE_EXPIRED, updateMetaSiteToken);
    },
    onDidUpdate(
      { loadingError: wasLoadingError },
      { loadUserSettings, loadingError, userSettings, setFirstLoadingDone },
    ) {
      if (wasLoadingError && !loadingError) {
        loadUserSettings();
      }

      if (userSettings.fulfilled) {
        setFirstLoadingDone(true);
      }
    },
    onWillUnmount({ appEvents, updateMetaSiteToken }) {
      appEvents.off(EVENT_INSTANCE_EXPIRED, updateMetaSiteToken);
    },
  }),
  branch(
    ({ userSettings, isFirstLoadingDone }) => (!userSettings || userSettings.pending) && !isFirstLoadingDone,
    renderNothing,
  ),
  withProps(({ userSettings }) => ({
    // use fallbackUserSettings to save reference to object, so mmgrSettings won't be updated
    // if user receives error and click "Try Again" button, which in this case sends 600 evid
    userSettings: pathOr<TUserSettings, TUserSettings>(fallbackUserSettings, ['value'], userSettings),
  })),
  withRouter,
  withPropsOnChange(['mmgrSettings'], ({ mmgrSettings, userSettings, location }) => {
    const translations: TIncomingTranslationsObject = parseMaybeObject(mmgrSettings.translations);

    const {
      namespace,
      publicMediaRoot,
      isUIAlignmentsEnabled,
      isHideInstagramEnabled,
      isHideFlickrEnabled,
      isHideGooglePhotosEnabled,
    } = mmgrSettings;

    const isGdprMode = RouteMatcher.isAll(location.pathname) || namespace === NameSpace.All;

    const isOpenedInSecureContext = isSecureContext(namespace) || isGdprMode;

    const hasVisitorUploads = userSettings.hasVisitorUploads;

    const parsedTabList = pipe<string, MainSource[], MainSource[]>(
      deserializeStringToArray,
      parseTabsList,
    )(mmgrSettings.tabsList);

    const excludedTabs = [];
    const tabsToReplace = {};

    if (isOpenedInSecureContext) {
      excludedTabs.push(MainSource.Home);
    }

    if (!isOpenedInSecureContext && contains(MainSource.Favorites, parsedTabList)) {
      tabsToReplace[MainSource.Favorites] = MainSource.Boards;
    }

    const tabsList = isOpenedInSecureContext
      ? selectTabsListForNamespace(namespace)
      : pipe<MainSource[], MainSource[], MainSource[]>(
          without(excludedTabs),
          map((oldTab) => (tabsToReplace[oldTab] ? tabsToReplace[oldTab] : oldTab)),
        )(parsedTabList);

    const initialMediaTypes = deserializeStringToArray<MediaType>(mmgrSettings.initialMediaTypes);
    const shouldAddShapes =
      join(',', deserializeStringToArray<MediaType>(mmgrSettings.mediaType)) === 'site_icon,picture';
    const mediaTypeWithShapes = `${mmgrSettings.mediaType},shape`;
    const mediaType = parseMediaType(namespace, shouldAddShapes ? mediaTypeWithShapes : mmgrSettings.mediaType);

    const gdprSettings = isGdprMode
      ? {
          addMediaButtonVisible: false,
          canSubmitItems: false,
          namespace: NameSpace.All,
          path: RouteBuilder.All(),
        }
      : {};

    const parsedPublicMediaRoot = deserializeStringToArray<WixMediaRoot>(publicMediaRoot);
    const path = mmgrSettings.path
      ? parseSettingsPath(mmgrSettings.path, tabsList, head(parsedPublicMediaRoot))
      : RouteBuilder.Source(head(tabsList));
    const pathSource = parsePath(path).source;

    let servicesList = pipe<string, ExternalSource[], ExternalSource[]>(
      deserializeStringToArray,
      filter(contained(SupportedExternalSourcesList)),
    )(mmgrSettings.servicesList);
    const filteredExternalServices = [
      ...(isHideInstagramEnabled ? [ExternalSource.Instagram] : []),
      ...(isHideFlickrEnabled ? [ExternalSource.Flickr] : []),
      ...(isHideGooglePhotosEnabled ? [ExternalSource.GooglePhotos] : []),
    ];
    if (filteredExternalServices.length) {
      servicesList = reject(contained(filteredExternalServices), servicesList);
    }

    const defaultServicesList = deserializeStringToArray<ExternalSource>(defaultMMGRSettings.servicesList);
    const externalSources = [ExternalSource.Computer, ...filter(contained(servicesList), defaultServicesList)];

    const getMediaButtonTranslationId = () => {
      if (isUIAlignmentsEnabled) {
        return isOpenedInSecureContext
          ? NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS[namespace]
          : 'categories.upload-media';
      }

      return isOpenedInSecureContext ? NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS_OLD[namespace] : 'categories.add-media';
    };

    return {
      mmgrSettings: {
        ...mmgrSettings,
        ...userSettings,
        // metaSiteToken from mmgrSettings is prioritized after recreating the token that was expired
        metaSiteToken: mmgrSettings.metaSiteToken || userSettings.metaSiteToken,
        maxParallelUploads: Number(mmgrSettings.maxParallelUploads),
        isSecureContext: isOpenedInSecureContext,
        mediaType,
        selection: mmgrSettings.selectionType,
        tabsList,
        externalSources,
        vision: uniq(['label', 'face', ...deserializeStringToArray(mmgrSettings.vision)]),
        path,
        publicMediaRoot: parsedPublicMediaRoot,
        hasVisitorUploads: hasVisitorUploads || isVisitorsUploads(pathSource),
        canSubmitItems: mmgrSettings.canShowSelectButton,
        addMediaButtonVisible: mmgrSettings.uploadItems,
        addMediaPanelOpened: mmgrSettings.uploadItemsOpen,
        servicesList,
        initialMediaTypes,
        translations: removeUndefinedValues({
          'common.header': translations.header,
          'actions.choose-files': translations['footer.submit'],
          ...translations,
        }),
        addMediaButtonTranslationId: getMediaButtonTranslationId(),
        siteFilesTranslationId: isOpenedInSecureContext
          ? NAME_SPACE_SITE_FILES_TRANSLATIONS[namespace]
          : 'categories.site-files',
        ...gdprSettings,
      },
    };
  }),
  onPropChange(
    isValueChanged(({ mmgrSettings }) => mmgrSettings.path),
    ({ mmgrSettings, history }) => {
      history.replace(mmgrSettings.path);
    },
    isStandalone,
  ),
  withContext(contextTypes, pick(['mmgrSettings', 'latestIncomingMMGRSettings'])),
  withoutRouter,
);

export const MMGRSettingsProvider = enhanceMMGRSettingsProvider(
  ({ mmgrSettings, latestIncomingMMGRSettings, children }) => (
    <MMGRContext.Provider value={{ mmgrSettings, latestIncomingMMGRSettings }}>{children}</MMGRContext.Provider>
  ),
);

export const withMMGRSettingsProvider = (BaseComponent) => (props) =>
  (
    <MMGRSettingsProvider>
      <BaseComponent {...props} />
    </MMGRSettingsProvider>
  );
